import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BookType } from 'xlsx'

import { Check, Close, Hardware } from '@mui/icons-material'
import { Box, Button, Card, CircularProgress, Divider, Grid, IconButton, TableContainer, Typography } from '@mui/material'

import { useGetUserProfileQuery } from '../../../../api/combined'
import { Tag } from '../../../../api/core'
import { ExportDataButton } from '../../../../components/ExportDataButton/ExportDataButton'
import { GRTable, GRTableColumn, SortOrder } from '../../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import GameIconWithPowerScore from '../../../../components/GameIconWithPowerScore/GameIconWithPowerScore'
import IAPRevenueImpact from '../../../../components/IAPRevenueImpact/IAPRevenueImpact'
import { intersection } from '../../../../helpers/intersection'
import { uniq } from '../../../../helpers/uniq'
import { useExportDataCompareGamesPageFeatureTabView } from '../../../../hooks/exportDataHooks'
import useGetScrollPosition from '../../../../hooks/useGetScrollPosition'
import analyticsService from '../../../../services/AnalyticsService'
import { RoleEnum } from '../../../account'
import { User } from '../../../account/types/User'
import { generateExport } from '../../../export-data/util/workbook'
import { AllFeatureCategoriesValue, FeatureCategorySelector } from '../../../feature/components/FeatureCategorySelector/FeatureCategorySelector'
import { FeatureLabelLink, FeatureLink } from '../../../feature/components/FeatureLink'
import { FeaturesAndKeywordsAutocomplete } from '../../../feature/components/FeaturesAndKeywordsAutocomplete/FeaturesAndKeywordsAutocomplete'
import { TagList } from '../../../feature/components/TagList/TagList'
import { useFeatureTags } from '../../../feature/hooks/useFeatureTags'
import { FeatureAndKeywordSearchResult } from '../../../feature/types/types'
import { Game, GameLink } from '../../../game'
import { CategoryImage } from '../../../game-features/components/CategoryImage/CategoryImage'
import { useGameFeatureKeywords } from '../../../game-features/hooks/gameFeaturesHooks'
import { GameWithMarketIso } from '../../../game-search/components/GameSearchDialog/GameSearchDialog'
import { GameAndAnalysis } from '../../../game/types/GameAndAnalysis'
import { useMobileSize } from '../../../responsiveness/hooks/responsivenessHooks'
import { useCompareFeatureFilterRows } from '../../hooks/useCompareFeatureFilterRows'
import { CompareFeatureRow, useCompareFeatureRows } from '../../hooks/useCompareFeatureRows'
import { useCompareMultiGames } from '../../hooks/useCompareMultiGames'
import FeatureSetSelection, { compareFeatureSetOpts as CompareFeatureSetOpts } from '../FeatureSetSelection/FeatureSetSelection'
import './FeatureTabView.scss'

const gameColumnHeaderClassName = 'GameComparisonTable__gameIconContainer'
interface SortedGameProps {
  selectedSortGameAndAnalysis?: GameAndAnalysis
  onSortGameAndAnalysisSelect?: (gameAndAnalysis?: GameAndAnalysis) => void
}

/**
 * GameIconWrapper
 */
interface GameIconWrapperProps extends SortedGameProps {
  gameAndAnalysis: GameAndAnalysis
}
const GameIconWrapper: React.FC<GameIconWrapperProps> = ({ selectedSortGameAndAnalysis, onSortGameAndAnalysisSelect, gameAndAnalysis }) => {
  const isGameSelected =
    selectedSortGameAndAnalysis &&
    selectedSortGameAndAnalysis.game.id === gameAndAnalysis.game.id &&
    selectedSortGameAndAnalysis.analysis?.marketIso === gameAndAnalysis.analysis?.marketIso

  return (
    <Box
      onClick={() => {
        if (!onSortGameAndAnalysisSelect) return
        if (isGameSelected) {
          onSortGameAndAnalysisSelect()
        } else {
          onSortGameAndAnalysisSelect(gameAndAnalysis)
        }
      }}
      className={classNames('GameComparisonTable__gameIconWrapper', {
        selected: isGameSelected,
      })}
      sx={{ p: 1, border: '1px solid', borderRadius: '5px' }}
    >
      <GameIconWithPowerScore game={gameAndAnalysis.game} marketIso={gameAndAnalysis.analysis?.marketIso} />
    </Box>
  )
}

type CompareGameTableRow = {
  keyValue: {
    [key: string]: number
  }
}

/**
 * GameComparisonTable
 */
interface GameComparisonTableProps extends SortedGameProps {
  isLoading?: boolean
  gamesAndAnalysis: GameAndAnalysis[]
  rows: CompareFeatureRow[]
}
const GameComparisonTable: React.FC<GameComparisonTableProps> = ({
  isLoading,
  gamesAndAnalysis,
  rows,
  selectedSortGameAndAnalysis,
  onSortGameAndAnalysisSelect,
}) => {
  const { t } = useTranslation()
  const { data: currentUser } = useGetUserProfileQuery()

  const initialColumns: GRTableColumn<CompareFeatureRow, CompareGameTableRow>[] = useMemo(() => {
    const rowContent = (game: Game, choice?: string, translatedChoice?: string) => {
      switch (choice?.trim().toLowerCase()) {
        case 'yes':
          return (
            <GameLink game={game} disabled={game.isOwnGame(currentUser as User)} subpage="implementations">
              <Check className="GameComparisonTable__rowContentIcon" fontSize="small" color="success" />
            </GameLink>
          )
        case 'no':
          return (
            <GameLink game={game} disabled={game.isOwnGame(currentUser as User)} subpage="implementations">
              <Close className="GameComparisonTable__rowContentIcon" fontSize="small" color="error" />
            </GameLink>
          )
        default:
          return (
            <GameLink game={game} disabled={game.isOwnGame(currentUser as User)} subpage="implementations" style={{ color: 'black' }}>
              {translatedChoice}
            </GameLink>
          )
      }
    }
    const gameColumns = gamesAndAnalysis.map(({ game, analysis }) => ({
      headerCellProps: { sx: { backgroundColor: 'white' } },
      columns: [
        {
          labelAccessor: (
            <Box className={gameColumnHeaderClassName}>
              <GameIconWrapper
                onSortGameAndAnalysisSelect={onSortGameAndAnalysisSelect}
                selectedSortGameAndAnalysis={selectedSortGameAndAnalysis}
                gameAndAnalysis={{ game, analysis }}
              />
            </Box>
          ),
          accessor: ({ row }: { row: CompareFeatureRow }) => {
            if (!row.gameColumns[game.id]) return null

            const { choice, gpsEffect, translatedChoice } = row.gameColumns[game.id]
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <GRTooltip content={translatedChoice}>
                  <Typography className={classNames('GameComparisonTable__choiceText', { zerovalue: gpsEffect === 0 })}>
                    {rowContent(game, choice, translatedChoice)}
                  </Typography>
                </GRTooltip>

                <IAPRevenueImpact showZeroRating dotValue={gpsEffect} />
              </Box>
            )
          },
          headerCellProps: { sx: { minWidth: '20px', backgroundColor: 'white' } },
        },
      ],
    }))

    return [
      {
        headerCellProps: { sx: { backgroundColor: 'white' } },
        columns: [
          {
            labelAccessor: t('common:feature'),
            accessor: ({ row }) => (
              <Grid container alignItems="center" gap={2}>
                <Grid item ml={1}>
                  <GRTooltip
                    content={
                      <>
                        <Typography variant="body1">{row.categoryName}</Typography>
                      </>
                    }
                  >
                    {row.icon && <CategoryImage src={row.icon.url} alt={row.featureName} />}
                  </GRTooltip>
                </Grid>
                <Grid item flexShrink={1}>
                  <FeatureLabelLink
                    feature={{ featureName: row.featureName, featureLegacyId: row.featureId }}
                    gameIds={row.implementationWithGameIds}
                    forceShowFeatureInList
                  />
                </Grid>
                {row.isDifferentChoice && (
                  <Grid item>
                    <GRTooltip content={t('compare:differing_features')}>
                      <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: (theme) => theme.palette.error.main }} />
                    </GRTooltip>
                  </Grid>
                )}
              </Grid>
            ),
            sortable: true,
            sortAccessor: ({ row }) => row.featureName,
            defaultSortOrder: SortOrder.ASC,
            headerCellProps: { sx: { minWidth: 250, backgroundColor: 'white' } },
            cellProps: { align: 'left' },
          },
          {
            accessor: ({ row }) => (
              <>
                {row.showImplementationFeature && (
                  <FeatureLink
                    feature={{ featureName: row.featureName, featureLegacyId: row.featureId }}
                    initialTab="implementation"
                    gameIds={row.implementationWithGameIds}
                    forceShowFeatureInList
                  >
                    <GRTooltip content={t('common:open_feature_implementation')}>
                      <IconButton className="FeatureTabView__icon" color="primary" component="span" size="small" sx={{ p: '4px' }}>
                        <Hardware sx={{ width: '16px', height: '16px' }} />
                      </IconButton>
                    </GRTooltip>
                  </FeatureLink>
                )}
              </>
            ),
            sortable: false,
            headerCellProps: { sx: { minWidth: 40, backgroundColor: 'white' } },
          },
        ],
      },
      ...gameColumns,
    ] as GRTableColumn<CompareFeatureRow, CompareGameTableRow>[]
  }, [currentUser, gamesAndAnalysis, onSortGameAndAnalysisSelect, selectedSortGameAndAnalysis, t])

  const [columns, setColumns] = useState<GRTableColumn<CompareFeatureRow, CompareGameTableRow>[]>(initialColumns)
  const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<CompareFeatureRow, CompareGameTableRow>[]) => {
    setColumns(updatedColumns)
  }, [])

  useEffect(() => {
    handleColumnsUpdate(initialColumns)
  }, [handleColumnsUpdate, initialColumns])

  return (
    <TableContainer>
      <GRTable
        tableProps={{ stickyHeader: true }}
        columns={columns}
        rows={rows}
        rowIdKey="uniqueId"
        onColumnsUpdated={handleColumnsUpdate}
        isLoading={isLoading}
        hoverable
        striped
        noRowsLabel={t('common:no_features_found')}
      />
    </TableContainer>
  )
}

/**
 * StickyGameIconBar
 */
interface StickyGameIconBarProps extends SortedGameProps {
  gamesAndAnalysis: GameAndAnalysis[]
}
const StickyGameIconBar: React.FC<StickyGameIconBarProps> = ({ gamesAndAnalysis, onSortGameAndAnalysisSelect, selectedSortGameAndAnalysis }) => {
  const { t } = useTranslation()
  const { scrollPosition } = useGetScrollPosition()
  const topBarHeight: number = document.getElementById('app-header')?.clientHeight as number
  const mobileSize = useMobileSize('lg')
  const showBar = scrollPosition > 780 && !mobileSize

  const containerRef = useRef<HTMLDivElement>(null)
  const [, forceRerender] = useReducer((x: number) => x + 1, 0)

  // The sticky bar positions each game icon over its table column. Those columns move/resize when the
  // sidebar Hide/Show animation runs, the window is resized, or the table is scrolled horizontally, but
  // none of the existing re-render triggers (vertical scroll, props, breakpoint) fire for those cases.
  // Observe a header element and listen for resize plus any scroll (capture phase, so the table
  // container's horizontal scroll is caught too) so the bar re-measures and keeps each icon centered.
  // The work only runs while the bar is visible, and re-renders are coalesced to one per animation frame
  // to keep scroll handling cheap.
  useEffect(() => {
    if (!showBar) return

    const headerElement = document.getElementsByClassName(gameColumnHeaderClassName)[0]

    let rafId: number | null = null
    const scheduleRemeasure = () => {
      if (rafId !== null) return
      rafId = window.requestAnimationFrame(() => {
        rafId = null
        forceRerender()
      })
    }

    const resizeObserver = new ResizeObserver(scheduleRemeasure)
    if (headerElement) {
      resizeObserver.observe(headerElement)
    }

    window.addEventListener('resize', scheduleRemeasure)
    document.addEventListener('scroll', scheduleRemeasure, true)

    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId)
      resizeObserver.disconnect()
      window.removeEventListener('resize', scheduleRemeasure)
      document.removeEventListener('scroll', scheduleRemeasure, true)
    }
  }, [gamesAndAnalysis.length, showBar])

  // Horizontal center of each game-column header, relative to the sticky bar, so every icon stays exactly
  // over its column regardless of the current table width (e.g. after the sidebar toggles or the window resizes).
  const containerRect = containerRef.current?.getBoundingClientRect()
  const gameColumnCenterOffset = (index: number): number | undefined => {
    const headerElement = document.getElementsByClassName(gameColumnHeaderClassName)[index]
    if (!headerElement || !containerRect) return undefined
    const headerRect = headerElement.getBoundingClientRect()
    return headerRect.left + headerRect.width / 2 - containerRect.left
  }

  return (
    <Box
      ref={containerRef}
      className="StickyGameIconBar__container"
      sx={{
        display: showBar ? 'block' : 'none',
        opacity: showBar ? 1 : 0,
        position: 'sticky',
        top: topBarHeight + 'px',
        zIndex: 5,
        // Tall enough to fully contain the game-icon wrapper (62px icon + padding + border) AND the
        // downward caret that appears below a selected icon, so neither the wrapper's bottom border nor
        // the caret overflows the bar onto the table below.
        minHeight: '110px',
        // Clip the icons to the bar's (table) width so the last icon can't overflow the table edge when
        // there isn't enough horizontal space for all game columns. An element's own box-shadow is not
        // clipped by its own overflow, so the card shadow below is preserved.
        overflow: 'hidden',
        backgroundColor: 'white',
        borderBottomLeftRadius: '20px',
        borderBottomRightRadius: '20px',
      }}
    >
      {gamesAndAnalysis.map(({ game, analysis }, index) => {
        const centerOffset = gameColumnCenterOffset(index)
        return (
          <Box
            key={`${game.id}-${analysis?.marketIso}`}
            className="StickyGameIconBar__iconContainer"
            sx={{
              position: 'absolute',
              // Nudge the icon group up by half the caret height so the icon + its (selected) down-arrow
              // read as vertically centered and the caret never clips against the bar's bottom edge.
              top: 'calc(50% - 6px)',
              left: centerOffset !== undefined ? `${centerOffset}px` : 0,
              transform: 'translate(-50%, -50%)',
              display: centerOffset !== undefined ? 'flex' : 'none',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <GameIconWrapper
              onSortGameAndAnalysisSelect={onSortGameAndAnalysisSelect}
              selectedSortGameAndAnalysis={selectedSortGameAndAnalysis}
              gameAndAnalysis={{ game, analysis }}
            />
          </Box>
        )
      })}
      {/* Opaque, full-height gutter that keeps the Back to top button above the icons: when the table is
          scrolled horizontally the leading game icons slide left and disappear cleanly behind it instead of
          overlapping the button. */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          px: 2,
          backgroundColor: 'white',
          borderBottomLeftRadius: '20px',
          zIndex: 2,
        }}
      >
        <Button
          variant="contained"
          // Keep the label on one line but bound the button (and therefore the gutter) width so a long
          // translation can never grow wide enough to permanently cover the first game column's icon.
          sx={{ whiteSpace: 'nowrap', maxWidth: '200px' }}
          onClick={() => {
            window.scrollTo({ top: 0, behavior: 'smooth' })
          }}
        >
          {t('common:back_to_top')}
        </Button>
      </Box>
    </Box>
  )
}

/**
 * FeatureTabView
 */
interface FeatureTabViewProps {
  gamesAndAnalysis: GameAndAnalysis[]
  gamesWithMarketIso: GameWithMarketIso
}
const FeatureTabView: React.FC<FeatureTabViewProps> = ({ gamesAndAnalysis, gamesWithMarketIso }) => {
  useEffect(() => {
    analyticsService.trackEvent('Visited Game Comparison: Features')
  }, [])

  const [selectedSortGameAndAnalysis, setSelectedSortGameAndAnalysis] = useState<GameAndAnalysis | undefined>()

  const gamesAndMarketIso = useMemo(() => {
    return Object.entries(gamesWithMarketIso).reduce<{ gameId: string; marketIso: string }[]>((result, [gameId, marketIsoList]) => {
      marketIsoList.forEach((marketIso) => {
        result.push({ gameId: gameId, marketIso })
      })
      return result
    }, [])
  }, [gamesWithMarketIso])

  const { data: multiGamesCompare, isLoading: isMultiGamesCompareLoading } = useCompareMultiGames({
    gameIds: gamesAndMarketIso.map(({ gameId }) => gameId),
    marketIsos: gamesAndMarketIso.map(({ marketIso }) => marketIso),
  })

  const [featureSearchValue, setFeatureSearchValue] = useState<FeatureAndKeywordSearchResult>(null)
  const [categoryFilter, setCategoryFilter] = useState<string>(AllFeatureCategoriesValue)
  const { data: featureTagGroups } = useFeatureTags()
  const allFeatureTags = useMemo(() => featureTagGroups?.flatMap((featureTag) => featureTag.tags) || [], [featureTagGroups])
  const [selectedFeatureTags, setSelectedFeatureTags] = useState<Tag[]>(allFeatureTags)

  const [featureSetOption, setFeatureSetOption] = useState<CompareFeatureSetOpts>(CompareFeatureSetOpts.all)
  const keywords = useGameFeatureKeywords()
  const [rows, isRowLoading] = useCompareFeatureRows(multiGamesCompare)

  const filteredRows = useCompareFeatureFilterRows({
    rows,
    featureTags: selectedFeatureTags,
    featureSearchValue,
    categoryId: categoryFilter,
    featureSetOption,
    keywords,
  })

  const [isExporting, setIsExporting] = useState(false)
  const [exportFormat, setExportFormat] = useState('csv' as BookType)

  const { exportRows } = useExportDataCompareGamesPageFeatureTabView(rows, gamesAndAnalysis, isExporting)

  const handleDataExport = useCallback((format: BookType) => {
    setExportFormat(format)
    setIsExporting(true)
  }, [])

  useEffect(() => {
    if (!isExporting || !exportRows.length) return
    setIsExporting(false)
    generateExport(exportFormat, exportRows, 'Game Comparison', `game-comparison`)
  }, [isExporting, exportRows, exportFormat])

  const filteredSelectedTags = useMemo(() => {
    const selectedTargets = selectedFeatureTags.map((selectedTag) => selectedTag.targets).flat()
    return [...new Set(selectedTargets)]
  }, [selectedFeatureTags])

  const sortedFilteredRows = useMemo(() => {
    // Apply tag filtering to the already filtered rows (which include search filtering)
    let filteredFeatures = filteredRows.filter((feature) => {
      if (!filteredSelectedTags || !filteredSelectedTags.length || !feature.featureId) {
        return true
      }
      return filteredSelectedTags.includes(feature.featureId)
    })

    // Then apply sorting if a game is selected for sorting
    if (!!selectedSortGameAndAnalysis) {
      return [...filteredFeatures].sort(
        (a, b) => b.gameColumns[selectedSortGameAndAnalysis.game.id].gpsEffect - a.gameColumns[selectedSortGameAndAnalysis.game.id].gpsEffect
      )
    }

    return filteredFeatures
  }, [filteredRows, filteredSelectedTags, selectedSortGameAndAnalysis])

  useEffect(() => {
    if (allFeatureTags.length > 0 && selectedFeatureTags.length === 0) {
      setSelectedFeatureTags(allFeatureTags)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allFeatureTags])

  const onSortGameAndAnalysisSelect = useCallback((gameAndAnalysis: GameAndAnalysis | undefined) => {
    setSelectedSortGameAndAnalysis(gameAndAnalysis)
  }, [])

  const handleSelectedFeatureTagsChange = useCallback((tags: Tag[]) => {
    setSelectedFeatureTags(tags)
  }, [])

  const handleClearSelectedFeatureTags = useCallback(() => {
    setSelectedFeatureTags([])
  }, [])

  const handleSelectAllFeatureTags = useCallback(() => {
    setSelectedFeatureTags(allFeatureTags)
  }, [allFeatureTags])

  const handleSelectTagGroup = useCallback(
    (tags: Tag[]) => {
      const currentlySelectedInGroup = intersection(selectedFeatureTags, tags)
      const isWholeTagGroupSelected = currentlySelectedInGroup.length === tags.length

      if (isWholeTagGroupSelected) {
        setSelectedFeatureTags(selectedFeatureTags.filter((selectedFeatureTag) => !tags.includes(selectedFeatureTag)))
      } else {
        setSelectedFeatureTags(uniq([...selectedFeatureTags, ...tags]))
      }
    },
    [selectedFeatureTags]
  )

  if (isRowLoading || isMultiGamesCompareLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!multiGamesCompare) {
    return null
  }

  return (
    <>
      <TagList
        tagGroups={featureTagGroups}
        onChange={handleSelectedFeatureTagsChange}
        selectedTags={selectedFeatureTags}
        onClear={handleClearSelectedFeatureTags}
        onSelectAll={handleSelectAllFeatureTags}
        onSelectTagGroup={handleSelectTagGroup}
      />
      <StickyGameIconBar
        selectedSortGameAndAnalysis={selectedSortGameAndAnalysis}
        onSortGameAndAnalysisSelect={(gameAndAnalysis) => setSelectedSortGameAndAnalysis(gameAndAnalysis)}
        gamesAndAnalysis={gamesAndAnalysis}
      />
      <Card sx={{ pt: 2, mt: 2 }}>
        <Grid container direction="row" justifyContent="space-between">
          <Box sx={{ px: 2 }}>
            <FeaturesAndKeywordsAutocomplete
              value={featureSearchValue}
              features={multiGamesCompare.features}
              keywords={keywords}
              onChange={(value) => {
                setFeatureSearchValue(value)
              }}
            />
            <Box sx={{ my: 2, display: 'flex', justifyContent: 'flex-start' }}>
              <Box sx={{ mr: 2 }}>
                <FeatureCategorySelector value={categoryFilter} onChange={(event) => setCategoryFilter(event.target.value)} />
              </Box>
              <FeatureSetSelection value={featureSetOption} onChange={(option) => setFeatureSetOption(option)} />
            </Box>
          </Box>
          <Box sx={{ mr: 2 }}>
            <ExportDataButton
              accessRoles={[RoleEnum.csv_export_games, RoleEnum.csv_game_comparison]}
              loading={!rows.length}
              onChooseFormat={handleDataExport}
              analyticsEventOrigin="Feature Tab View"
            />
          </Box>
        </Grid>
        {!!rows && (
          <>
            <Divider />
            <GameComparisonTable
              selectedSortGameAndAnalysis={selectedSortGameAndAnalysis}
              onSortGameAndAnalysisSelect={onSortGameAndAnalysisSelect}
              rows={sortedFilteredRows}
              gamesAndAnalysis={gamesAndAnalysis}
              isLoading={!rows.length}
            />
          </>
        )}
      </Card>
    </>
  )
}

export default FeatureTabView
